// hooks/useAllMomentum.js
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Module-level cache — survives unmount/remount
let cachedPayload = null;
let cachedAt = 0;
const CLIENT_TTL = 60 * 1000;

const useAllMomentum = (autoRefreshMs = 60000) => {
  const [data, setData] = useState(cachedPayload?.data || []);
  const [loading, setLoading] = useState(!cachedPayload);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(
    cachedPayload ? new Date(cachedAt) : null,
  );
  const intervalRef = useRef(null);

  const fetchAll = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const { data: res } = await axios.get(
        `${BASE_URL}/api/screener/momentum/all`,
      );
      if (res.success) {
        cachedPayload = res;
        cachedAt = Date.now();
        setData(res.data || []);
        setLastUpdated(new Date());
      } else {
        setError(res.message || "Failed to fetch");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchAll(!cachedPayload);
  }, [fetchAll]);

  // Auto-refresh (only when tab is visible)
  useEffect(() => {
    if (autoRefreshMs > 0) {
      intervalRef.current = setInterval(() => {
        if (document.visibilityState === "visible") {
          fetchAll(false);
        }
      }, autoRefreshMs);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [autoRefreshMs, fetchAll]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    refresh: () => fetchAll(true),
  };
};

export default useAllMomentum;
